import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, ArrowRight } from "lucide-react";
import { NL_AREAS, RIVIERA_CITIES, rivieraCityLink } from "@/lib/riviera";

export default function ServiceAreas() {
  const { t } = useTranslation();
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  }, [hash]);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20" data-testid="service-areas-page">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5BA83F]">{t("areas_page.eyebrow")}</p>
      <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1B3A6B] mt-3 tracking-tight">{t("areas_page.title")}</h1>
      <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">{t("areas_page.intro")}</p>

      <AreaGroup id="netherlands" title={t("areas_page.nl_heading")} note={t("areas_page.nl_note")}>
        {NL_AREAS.map((a) => (
          <Link key={a.slug} to={`/service-areas/${a.slug}`} id={a.slug} className="group scroll-mt-24 bg-white rounded-xl p-5 ring-1 ring-slate-200 hover:bg-[#E8F5E4]/50 transition-colors" data-testid={`area-${a.city}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-[#1B3A6B] font-heading font-semibold text-lg">
                <MapPin className="w-4 h-4 text-[#5BA83F]" /> {a.city}
              </span>
              <ArrowRight className="w-4 h-4 text-[#5BA83F] transition-transform group-hover:translate-x-1" />
            </div>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.neighborhoods}</p>
          </Link>
        ))}
      </AreaGroup>

      <AreaGroup id="french-riviera" title={t("areas_page.fr_heading")} note={t("areas_page.fr_note")}>
        {RIVIERA_CITIES.map((c) => (
          <Link key={c.slug} to={rivieraCityLink(c)} id={`fr-${c.slug}`} data-testid={`area-fr-${c.slug}`}
            className="group scroll-mt-24 bg-white rounded-xl p-5 ring-1 ring-sky-200 hover:bg-sky-50/60 transition-colors">
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-[#1B3A6B] font-heading font-semibold text-lg">
                <MapPin className="w-4 h-4 text-[#0EA5E9]" /> {c.name}
              </span>
              {c.inquiryOnly
                ? <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-testid="area-fr-monaco-badge">{t("riviera.inquiry_only")}</span>
                : <ArrowRight className="w-4 h-4 text-[#0EA5E9] transition-transform group-hover:translate-x-1" />}
            </div>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(`areas_page.fr_areas.${c.slug}`)}</p>
          </Link>
        ))}
      </AreaGroup>
    </div>
  );
}

function AreaGroup({ id, title, note, children }) {
  return (
    <section id={id} className="mt-12 scroll-mt-24" data-testid={`areas-group-${id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-5">
        <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A]">{title}</h2>
        <span className="text-xs font-semibold text-slate-500">{note}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </section>
  );
}
